'use client'

import {
  IconAlertCircleFill,
  IconCheckCircleFill,
  IconClock,
  IconSpot,
} from '@macarongfactory/web-icons'
import { Text } from '@macarongfactory/web-ui'
import { Fragment, useRef } from 'react'

import { BUSINESS_HOUR_LABELS } from '@/features/seoPlaceDetail/lib'
import { useImpressionLog } from '@/features/seoPlaceDetail/model'
import { type ISeoPlace } from '@/features/seoPlaceDetail/model'
import {
  SNACKBAR_BOTTOM_OFFSET_WITH_CTA,
  SNACKBAR_DEFAULT_BOTTOM_OFFSET,
} from '@/shared/design-system/components/Snackbar'
import { CODE, EVENT_NAMES, LABELS, TYPE } from '@/shared/lib'
import useSnackbarStore from '@/shared/model/useSnackbarStore'
import { NaverMap } from '@/shared/ui'

import PlaceSectionDivider from '../PlaceSectionDivider/PlaceSectionDivider'

const SECTION_IMPRESSION_PARAMS = {
  code: CODE.COMMON_WEB_PLACE.SHOP_INFO,
  type: TYPE.SECTION,
  label: LABELS.COMMON_WEB_PLACE.SHOP_INFO,
}

interface IPlaceBusinessInfoProps {
  place: ISeoPlace
}

const PlaceBusinessInfo = ({ place }: IPlaceBusinessInfoProps) => {
  const address = place.coordinates?.streetAddress || place.coordinates?.address
  const latitude = place.coordinates?.latitude
  const longitude = place.coordinates?.longitude
  const businessHours = place.businessHours

  const {
    actions: { showSnackbar },
  } = useSnackbarStore()

  const getBottomOffset = () => {
    // PC(lg+)에서는 하단 모바일 CTA가 숨겨지므로 기본 오프셋 사용
    if (typeof window === 'undefined') return SNACKBAR_BOTTOM_OFFSET_WITH_CTA
    return window.matchMedia('(min-width: 1024px)').matches
      ? SNACKBAR_DEFAULT_BOTTOM_OFFSET
      : SNACKBAR_BOTTOM_OFFSET_WITH_CTA
  }

  const handleCopyAddress = async () => {
    if (!address) return
    try {
      await navigator.clipboard.writeText(address)
      showSnackbar({
        text: '주소가 복사되었어요.',
        bottomOffset: getBottomOffset(),
        contentClassName: 'mx-dimension-x5 max-w-[460px] lg:mx-auto',
        leadingIcon: (
          <IconCheckCircleFill
            width={20}
            height={20}
            className='text-foreground-success-strong'
          />
        ),
      })
    } catch (error) {
      console.warn('[PlaceBusinessInfo] clipboard copy failed', error)
      showSnackbar({
        text: '주소 복사에 실패했어요.',
        bottomOffset: getBottomOffset(),
        contentClassName: 'mx-dimension-x5 max-w-[460px] lg:mx-auto',
        leadingIcon: (
          <IconAlertCircleFill
            width={20}
            height={20}
            className='text-foreground-danger-strong'
          />
        ),
      })
    }
  }

  const sectionRef = useRef<HTMLElement | null>(null)
  useImpressionLog({
    ref: sectionRef,
    eventName: EVENT_NAMES.COMMON_WEB_PLACE.SECTION.SHOW,
    params: SECTION_IMPRESSION_PARAMS,
  })

  return (
    <>
      <PlaceSectionDivider />
      <section
        ref={sectionRef}
        aria-label='업체 정보'
        className='flex flex-col gap-dimension-x4 bg-background-surface-default px-dimension-x5 py-dimension-x6'
      >
        <Text
          as='h2'
          variant='title-1-semibold'
          color='text-foreground-neutral-strong'
        >
          업체 정보
        </Text>

        <div className='flex flex-col gap-dimension-x3'>
          {address && (
            <div className='flex items-start gap-dimension-x2'>
              <div className='flex items-center py-dimension-x0-5'>
                <IconSpot
                  width={16}
                  height={16}
                  className='text-foreground-neutral-alternative'
                />
              </div>
              <div className='flex min-w-0 flex-wrap items-center gap-dimension-x0-5'>
                <Text
                  variant='body-2-regular'
                  color='text-foreground-neutral-strong'
                  className='break-keep'
                >
                  {address}
                </Text>
                <button
                  type='button'
                  onClick={handleCopyAddress}
                  className='cursor-pointer px-dimension-x1 py-dimension-x0-5'
                >
                  <Text
                    variant='body-2-medium'
                    color='text-foreground-brand-strong'
                  >
                    복사
                  </Text>
                </button>
              </div>
            </div>
          )}

          {latitude !== undefined && longitude !== undefined && (
            // NAVER SDK가 그리는 copyright 로고가 다이얼로그 위로 새는 문제 — stacking context 격리
            <div className='isolate h-[140px] overflow-hidden rounded-dimension-x3'>
              <NaverMap
                mapId={`place-business-map-${place.serverId}`}
                latitude={latitude}
                longitude={longitude}
                height={140}
                zoom={16}
                showMarker
              />
            </div>
          )}

          {businessHours && Object.keys(businessHours).length > 0 && (
            <div className='flex min-w-0 items-start gap-dimension-x2'>
              <div className='flex items-center py-dimension-x0-5'>
                <IconClock
                  width={16}
                  height={16}
                  className='text-foreground-neutral-alternative'
                />
              </div>
              <div className='grid min-w-0 flex-1 grid-cols-[max-content_1fr] gap-x-dimension-x2 gap-y-dimension-x1'>
                {Object.entries(businessHours).map(([day, hours]) => {
                  const isOff = /휴무/.test(day)
                  return (
                    <Fragment key={day}>
                      <Text
                        variant='body-2-medium'
                        color='text-foreground-neutral-strong'
                        className='break-keep'
                      >
                        {BUSINESS_HOUR_LABELS[day] ?? day}
                      </Text>
                      <Text
                        variant='body-2-regular'
                        color={
                          isOff
                            ? 'text-foreground-danger-strong'
                            : 'text-foreground-neutral-strong'
                        }
                        className='min-w-0 break-keep'
                      >
                        {hours}
                      </Text>
                    </Fragment>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  )
}

export default PlaceBusinessInfo
